import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import BackButton from "../../components/common/BackButton";
import CustomiseSection from "../../components/collection/CustomiseSection";

// 1. The Individual Product Card Component
const EmbroideryCard = ({ product, onViewClick }) => {
    const [loupePos, setLoupePos] = useState({ x: 0, y: 0 });
    const [imagePos, setImagePos] = useState({ x: 0, y: 0 });
    const imageWrapperRef = useRef(null);

    // This function calculates the mouse position relative to the image container
    // and updates the position of the loupe circle and the image inside it.
    const handleMouseMove = (e) => {
        const { left, top, width, height } =
            imageWrapperRef.current.getBoundingClientRect();

        // Mouse coordinates relative to the wrapper
        const x = e.clientX - left;
        const y = e.clientY - top;

        // Update Loupe Position (centered on mouse)
        setLoupePos({ x, y });

        // Update magnified image position *inside* the loupe.
        // New Formula:
        // We want the point (x, y) on the small image to correspond to the center of the loupe.
        // The large image is at -x * scale, -y * scale relative to the origin.
        // We add loupeRadius to center that 'view' in the loupe div.

        const scale = 2.8; // Matches CSS width: 250%
        const loupeRadius = 90; // Matches CSS width/height: 180px / 2 = 90

        setImagePos({
            x: -(x * scale) + loupeRadius,
            y: -(y * scale) + loupeRadius,
        });
    };

    return (
        <div className="pe-card-wrapper">
            <div className="pe-card pe-static-card">
                {/* --- Image Wrapper with Mouse Tracking --- */}
                <div
                    className="pe-image-wrapper"
                    ref={imageWrapperRef}
                    onMouseMove={handleMouseMove}
                // On mouse leave, maybe reset? Optional, but feels smoother if left as is.
                >
                    {/* The "Base" Photo (Flat) */}
                    <img
                        src={product.imageBase}
                        alt={product.title}
                        className="pe-base-image"
                    />

                    {/* The Magnifying Loupe Circle */}
                    <div
                        className="pe-loupe"
                        style={{ top: `${loupePos.y}px`, left: `${loupePos.x}px` }}
                    >
                        {/* The "Textured" Embroidery Image inside the loupe */}
                        <img
                            src={product.imageTexture}
                            alt="Embroidery Detail"
                            className="pe-loupe-image"
                            style={{
                                transform: `translate(${imagePos.x}px, ${imagePos.y}px)`,
                            }}
                        />
                    </div>
                </div>

                {/* --- Card Details --- */}
                <div className="pe-details">
                    {/* Replaced Category with ID */}
                    <span className="pe-category">PRODUCT ID: {product.id}</span>
                    <h3 className="pe-title">{product.title}</h3>

                    <div className="pe-button-container">
                        <button className="pe-btn" onClick={() => onViewClick(product)}>Click to View</button>
                    </div>
                </div>
            </div>
        </div>
    );
};




// 2. Main Container displaying the grid
export default function Shirts() {
    const navigate = useNavigate();

    // DUMMY DATA - Replace with your real data
    // IMPT: Use real, high-res embroidery closeups for 'imageTexture'
    const products = [
        {
            id: 'SH-01',
            category: "Minimal Shirt Embroidery",
            title: "Subtle Dots",
            price: "₹ 1,999",
            imageBase: "images/acc3.webp",
            imageTexture: "images/acc3.webp",
            description: "A clean and minimal embroidered shirt designed for everyday elegance. Perfect for those who appreciate simplicity with handcrafted charm.",
            highlights: ["Minimal hand embroidery", "Soft cotton fabric", "Perfect for daily wear"]
        },
        {
            id: 'SH-02',
            category: "Floral Shirt Embroidery",
            title: "Bloom in Detail",
            price: "₹ 1,799",
            imageBase: "images/carousel-4.webp",
            imageTexture: "images/carousel-4.webp",
            description: "Delicate floral embroidery placed thoughtfully to add freshness and warmth. A graceful blend of color and craftsmanship.",
            highlights: ["Hand-embroidered florals", "Premium fabric base", "Elegant & lightweight"]
        },
        {
            id: 'SH-03',
            category: "Nature-Inspired Embroidery",
            title: "Wildflower Touch",
            price: "₹ 1,799",
            imageBase: "images/shirt2.webp",
            imageTexture: "images/shirt2.webp",
            description: "Inspired by nature, this embroidered shirt features soft floral accents that bring life to your everyday style.",
            highlights: ["Nature-inspired motifs", "Durable stitching", "Comfortable fit"]
        },
        {
            id: 'SH-04',
            category: "Classic Shirt Embroidery",
            title: "Graceful Florals",
            price: "₹ 1,499",
            imageBase: "images/shirt3.webp",
            imageTexture: "images/shirt3.webp",
            description: "A timeless white shirt elevated with subtle floral embroidery. Perfect for casual outings and elegant gatherings.",
            highlights: ["Classic color palette", "Fine hand stitching", "Versatile design"]
        },
        {
            id: 'SH-05',
            category: "Typography Embroidery",
            title: "Cultural Threads",
            price: "₹ 1,499",
            imageBase: "images/shirt4.webp",
            imageTexture: "images/shirt4.webp",
            description: "A bold embroidered statement blending language and culture. Designed for those who love expressive fashion.",
            highlights: ["Custom text embroidery", "Strong thread finish", "Unique statement piece"]
        },
        {
            id: 'SH-06',
            category: "Personalized Shirt Embroidery",
            title: "Words That Matter",
            price: "₹ 1,499",
            imageBase: "images/shirt5.webp",
            imageTexture: "images/shirt5.webp",
            description: "Personalized embroidery that speaks your story. A perfect balance of meaning and modern style.",
            highlights: ["Customizable text", "Premium cotton fabric", "Handcrafted finish"]
        },
        {
            id: 'SK-01',
            category: "Kids Wear Embroidery",
            title: "Little Bloom Dress",
            price: "₹ 1,499",
            imageBase: "images/skirt.webp",
            imageTexture: "images/skirt.webp",
            description: "A charming embroidered dress crafted with soft fabric and playful floral details, perfect for little ones.",
            highlights: ["Gentle hand embroidery", "Soft & breathable fabric", "Kid-friendly design"]
        },
    ];


    const handleViewProduct = (product) => {
        navigate('/product-view', { state: { product } });
    };

    return (
        <>
            <section className="pe-section">
                <div style={{ padding: "0 5%", maxWidth: "1200px", margin: "0 auto", marginTop: "20px" }}>
                    <BackButton />
                </div>
                <div className="pe-header">
                    <h2 className="pe-main-title">❖ Shirts & Skirts Collection ❖</h2>
                    <div className="pe-thread-line"></div>
                    <p className="pe-subtitle">
                        Elevate everyday wear with finely embroidered shirts, crafted with precision and personal detail. A perfect blend of style and individuality.
                    </p>
                </div>

                <div className="pe-grid">
                    {products.map((product) => (
                        <EmbroideryCard key={product.id} product={product} onViewClick={handleViewProduct} />
                    ))}

                </div>
            </section>
            <CustomiseSection />
        </>
    );
}

